import React, { useMemo } from "react";
import { Alert, Badge, Button, Spinner, Table } from "react-bootstrap";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import toast from "react-hot-toast";
import {
  useDeleteHourPackage,
  useHourPackages,
} from "../hooks/useHourPackages";
import { PriceWithDiscount } from "../../../components/PriceWithDiscount";

const BILLING_LABELS = {
  MONTHLY: "Mensual",
  ANNUAL: "Anual",
  ONE_OFF: "Único",
};

function HourPackageList({ onEdit }) {
  const { data: pkgs, isLoading, isError } = useHourPackages();
  const deleteMutation = useDeleteHourPackage();

  const handleDelete = (pkg) => {
    if (!window.confirm(`¿Eliminar el paquete "${pkg.name}"?`)) return;
    deleteMutation.mutate(pkg.id, {
      onSuccess: () => toast.success("Paquete eliminado"),
      onError: () => toast.error("Error al eliminar el paquete"),
    });
  };

  const columns = useMemo(
    () => [
      { accessorKey: "name", header: "Nombre" },
      {
        accessorKey: "billing_cycle",
        header: "Ciclo",
        cell: ({ getValue }) => BILLING_LABELS[getValue()] ?? getValue(),
      },
      {
        accessorKey: "hours_included",
        header: "Horas",
        cell: ({ getValue }) => `${getValue()} h`,
      },
      {
        id: "price",
        header: "Precio",
        cell: ({ row }) => (
          <PriceWithDiscount
            listPrice={row.original.price}
            discountPct={row.original.discount_pct}
            finalPrice={row.original.final_price}
            size="sm"
          />
        ),
      },
      {
        accessorKey: "validity_days",
        header: "Vigencia (días)",
        cell: ({ getValue }) => getValue() ?? "—",
      },
      {
        accessorKey: "allowed_absence_hours",
        header: "Inasistencia permitida",
        cell: ({ getValue }) => `${Number(getValue() ?? 0)} h`,
      },
      {
        accessorKey: "is_active",
        header: "Activo",
        cell: ({ getValue }) => (
          <Badge bg={getValue() ? "success" : "secondary"}>
            {getValue() ? "Sí" : "No"}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "Acciones",
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              style={{ minHeight: 44 }}
              onClick={() => onEdit(row.original)}
            >
              Editar
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              style={{ minHeight: 44 }}
              onClick={() => handleDelete(row.original)}
              disabled={deleteMutation.isPending}
            >
              Eliminar
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteMutation.isPending]
  );

  const table = useReactTable({
    data: pkgs ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <Spinner animation="border" role="status" aria-label="Cargando paquetes" />;
  if (isError) return <Alert variant="danger">Error al cargar los paquetes.</Alert>;

  const rows = table.getRowModel().rows;

  return (
    <>
      {/* Desktop */}
      <div className="table-responsive d-none d-sm-block">
        <Table striped bordered hover className="mb-0">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-muted py-3">
                  Sin paquetes registrados
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Mobile: tarjetas */}
      <div className="d-sm-none">
        {(pkgs ?? []).length === 0 && (
          <p className="text-muted text-center py-3">Sin paquetes registrados</p>
        )}
        {(pkgs ?? []).map((pkg) => (
          <div key={pkg.id} className="card mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="card-title mb-0">{pkg.name}</h6>
                <Badge bg={pkg.is_active ? "success" : "secondary"}>
                  {pkg.is_active ? "Activo" : "Inactivo"}
                </Badge>
              </div>
              <p className="mb-1">
                <strong>Ciclo:</strong> {BILLING_LABELS[pkg.billing_cycle] ?? pkg.billing_cycle}
              </p>
              <p className="mb-1">
                <strong>Horas:</strong> {pkg.hours_included} h
              </p>
              <p className="mb-1">
                <strong>Precio:</strong>{" "}
                <PriceWithDiscount
                  listPrice={pkg.price}
                  discountPct={pkg.discount_pct}
                  finalPrice={pkg.final_price}
                  size="sm"
                />
              </p>
              {pkg.validity_days ? (
                <p className="mb-1"><strong>Vigencia:</strong> {pkg.validity_days} días</p>
              ) : null}
              <p className="mb-3">
                <strong>Inasistencia permitida:</strong>{" "}
                {Number(pkg.allowed_absence_hours ?? 0)} h
              </p>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  style={{ minHeight: 44, flex: 1 }}
                  onClick={() => onEdit(pkg)}
                >
                  Editar
                </Button>
                <Button
                  variant="outline-danger"
                  style={{ minHeight: 44, flex: 1 }}
                  onClick={() => handleDelete(pkg)}
                  disabled={deleteMutation.isPending}
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export { HourPackageList };
